/**
 * Identity-defect vocabulary and classifiers (SK-0).
 *
 * ## The defect this exists for
 *
 * `bluffcitywebdesigns`'s stored `businessName` is the literal string
 * `"About"` — the profiler read `about.html` as the home page and took the
 * leading `<title>` segment. `makeTextPairs` then rewrote every occurrence of
 * the word "About" to the client's business name (a real composed nav read
 * `Home · UI Compass · Services`), while the template's actual name survived
 * 201 times.
 *
 * The scanner already knew "About" is not identity — `buildTermCatalog`
 * rejects it. The substituter never asked. This module is the one place that
 * vocabulary and those predicates live, so a consumer cannot hold a private
 * copy and disagree with the others.
 *
 * ## Two vocabularies, deliberately not merged
 *
 * `GENERIC_SOURCE_TERMS` is the CATALOG's reject list: a term that must never
 * become a contamination term because it would fire on ordinary page copy.
 * Widening it changes what the scanner blocks on, so it is moved here
 * byte-for-byte and must only change with its own evidence.
 *
 * `NAV_WORDS` is the CLASSIFIER's vocabulary: words that indicate an
 * extraction read a navigation label instead of a business name. It is a
 * superset, it is free to grow, and it never reaches the catalog.
 *
 * Pure, no imports, Worker-safe. Do not add one.
 */

/**
 * The catalog's reject list. Moved verbatim from `ai/contamination-scan.js`,
 * which now imports it. Membership is behaviour: adding a word here stops the
 * scanner blocking on it.
 */
export const GENERIC_SOURCE_TERMS = new Set([
  'home',
  'about',
  'contact',
  'services',
  'reviews',
  'gallery',
  'privacy',
  'terms',
  'copyright'
]);

/**
 * Navigation and page labels an extractor mistakes for a business name.
 * A superset of GENERIC_SOURCE_TERMS; used only for classification.
 */
export const NAV_WORDS = new Set([
  ...GENERIC_SOURCE_TERMS,
  'about us',
  'contact us',
  'our services',
  'our story',
  'our work',
  'welcome',
  'menu',
  'blog',
  'news',
  'faq',
  'faqs',
  'team',
  'our team',
  'meet the team',
  'pricing',
  'prices',
  'packages',
  'shop',
  'store',
  'products',
  'portfolio',
  'projects',
  'work',
  'locations',
  'location',
  'careers',
  'resources',
  'testimonials',
  'book now',
  'book online',
  'free consultation',
  'schedule consultation',
  'book a consultation',
  'request consultation',
  'get a quote',
  'request a quote',
  'sitemap',
  'login',
  'log in',
  'cart',
  'account',
  'search',
  'index',
  'untitled',
  'unknown business'
]);

/**
 * CTA and navigation vocabulary that appears in a scraped "address" when the
 * extractor swallowed a contact strip. Classify by VOCABULARY, never by
 * length: `"663 S Rancho Santa Fe Rd #615"` is a legitimate 29-character
 * street address in this corpus and a length rule flags it.
 */
export const CTA_ADDRESS_MARKERS = [
  'click to email',
  'click to call',
  'email us',
  'call us',
  'message',
  'send a message',
  'location',
  'locations',
  'menu',
  'get directions',
  'opening hours',
  'follow us'
];

/**
 * Short, common words that must never carry `high` severity in the term
 * catalog. `Gyros` is a 5-character high-severity `name` term, so ANY client
 * whose copy contains the word hard-fails `CONTAMINATION_DETECTED` — and
 * because the scrubber deliberately exempts `class`/`id`/`style`, a hit in
 * `class="gyros"` can never be cleared. That is a deadlock, not a gate.
 */
export const OVERBROAD_TERM_STOPLIST = new Set([
  'gyros',
  'pizza',
  'tacos',
  'coffee',
  'salon',
  'studio',
  'clinic',
  'dental',
  'legal',
  'roofing',
  'plumbing',
  'fitness',
  'bakery',
  'garden',
  'design',
  'digital',
  'media',
  'group',
  'works',
  'house',
  'center',
  'centre',
  'church',
  'market',
  'kitchen'
]);

/** Longest term length that OVERBROAD_TERM_STOPLIST is consulted for. */
export const OVERBROAD_MAX_LENGTH = 6;

/**
 * Words a DOMAIN carries that a business name legitimately omits. Used only to
 * suppress a false `name-truncated-domain`: the name is not a truncation when
 * the only thing it drops is one of these.
 */
export const HOST_FILLER = new Set([
  'demo',
  'inc',
  'llc',
  'ltd',
  'corp',
  'group',
  'co',
  'online',
  'site',
  'web',
  'www',
  'usa',
  'official',
  'app',
  'hq',
  'the'
]);

/** Ordered, frozen — the census renders defect classes in this order. */
export const DEFECT_IDS = Object.freeze([
  'name-entity',
  'name-navword',
  'name-truncated-domain',
  'name-seo-phrase',
  'name-generic',
  'name-below-catalog-floor',
  'address-cta-junk',
  'catalog-blind',
  'catalog-overbroad',
  'oracle-uncovered'
]);

/** One-line human explanation per defect id, for gate output and the census. */
export const DEFECT_LABELS = Object.freeze({
  'name-entity': 'businessName still carries an HTML entity',
  'name-navword': 'businessName is a navigation or page label',
  'name-truncated-domain': 'businessName is a fragment of the domain, not the business',
  'name-seo-phrase': 'businessName is an SEO phrase, not a name',
  'name-generic': 'businessName is the domain-derived fallback or a placeholder',
  'name-below-catalog-floor': 'businessName is under 4 chars, so the catalog drops it entirely',
  'address-cta-junk': 'addressLines[0] is a scraped contact strip, not an address',
  'catalog-blind': 'the term catalog has no name entry for this template',
  'catalog-overbroad': 'a high-severity term is a short common word and cannot be scrubbed',
  'oracle-uncovered': "the template's real name is not covered by any cataloged term"
});

const ENTITY_RE = /&(?:#\d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]{1,9});/;

function clean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Comparison key: lowercase, alphanumerics only. "Bluff City Web Designs" and
 * "bluffcitywebdesigns" squash to the same string, which is what lets domain
 * affinity be measured without tokenisation guesswork.
 */
export function normalizeNameKey(value) {
  return clean(value)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .replace(/[^a-z0-9]+/g, '');
}

/** The first DNS label of a domain, `www.` stripped. */
export function hostLabel(domain) {
  return clean(domain)
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .split('.')[0];
}

/** Word count on whitespace. */
function wordCount(value) {
  return clean(value).split(/\s+/).filter(Boolean).length;
}

/**
 * Defect ids for one business name. Returns [] when the name looks usable.
 *
 * @param {string} name
 * @param {{sourceDomain?: string, nameConfidence?: string, nameSource?: string}} [opts]
 * @returns {string[]}
 */
export function classifyBusinessName(name, opts = {}) {
  const value = clean(name);
  const defects = [];
  if (!value) return ['name-below-catalog-floor'];

  if (ENTITY_RE.test(value)) defects.push('name-entity');
  if (value.length < 4) defects.push('name-below-catalog-floor');
  if (NAV_WORDS.has(value.toLowerCase())) defects.push('name-navword');
  if (value.length > 60 || wordCount(value) >= 6) defects.push('name-seo-phrase');

  const label = hostLabel(opts.sourceDomain);
  const key = normalizeNameKey(value);
  if (label && key) {
    // A SINGLE token that is a strict fragment of the domain — "Gyros" out of
    // "abshillbillygyros". Multi-token names are exempt on purpose: "Caliber
    // Plumbing" is a strict substring of "caliberplumbingservice" and is the
    // correct answer, so a bare substring rule would flag a good name.
    //
    // But only when what the name OMITS is substantive. A domain routinely
    // carries filler a business name does not: `greasewisedemo` is a demo
    // site, `westcon-inc` a legal suffix, `imresolute` an "I'm" prefix,
    // `traveltechsc` a state code. Flagging those condemns the right answer —
    // the same false-positive trap as classifying a junk address by LENGTH.
    // `patriotplasteringandconstruction` → "Patriot" drops the entire trade and
    // is a real truncation.
    if (wordCount(value) === 1 && label.includes(key) && key.length < label.length) {
      const remainder = label.replace(key, '').replace(/[^a-z0-9]/g, '');
      if (remainder.length > 2 && !HOST_FILLER.has(remainder)) {
        defects.push('name-truncated-domain');
      }
    }
  }

  // `name-generic` needs POSITIVE evidence that nothing was extracted, never a
  // resemblance to the domain. A squash comparison looks tempting and is
  // wrong in both directions: "Bluff City Web Designs" squashes to exactly
  // `bluffcitywebdesigns`, so it would condemn the correct answer — a gate
  // built on that rule can never go green — and "Shouldrs" is genuinely both
  // the business name and the domain label. Only an explicit placeholder, or a
  // profile that was never extracted at all, is evidence.
  if (value.toLowerCase() === 'unknown business') defects.push('name-generic');
  else if (opts.profileSource === 'stub' && label && key === label) defects.push('name-generic');

  return defects;
}

/**
 * Defects that make a name unsafe to use as a SUBSTITUTION source.
 *
 * `name-generic` is deliberately absent: a stub profile's domain-derived name
 * may still be the business's real name, and refusing to substitute it would
 * leave more source identity in the page, not less. It is a census signal — a
 * profile that needs regenerating — not a substitution hazard.
 */
export const SUBSTITUTION_BLOCKING_DEFECTS = Object.freeze([
  'name-entity',
  'name-navword',
  'name-truncated-domain',
  'name-seo-phrase',
  'name-below-catalog-floor'
]);

/**
 * True when a name is safe to use as a SUBSTITUTION source. This is the
 * predicate `makeTextPairs` consults before emitting a name pair and that
 * `buildTermCatalog` consults before admitting a name term.
 */
export function isPlausibleBusinessName(name, opts = {}) {
  const defects = classifyBusinessName(name, opts);
  // A score-clearing profiler result is positive evidence that can distinguish
  // a real compact brand (`JDog`, `ProWash`, `Tri-Lakes`) from a random host
  // fragment, and a real long event name from an SEO title. It NEVER waives a
  // nav word, entity, generic fallback, or below-floor name. Variants do not
  // inherit this evidence; callers pass it only for the chosen primary.
  const scoredPrimary =
    opts.nameConfidence === 'high' && clean(opts.nameSource) !== 'domain-fallback';
  const evidenceOverridable = new Set(['name-truncated-domain', 'name-seo-phrase']);
  return !defects.some(
    (id) =>
      SUBSTITUTION_BLOCKING_DEFECTS.includes(id) && !(scoredPrimary && evidenceOverridable.has(id))
  );
}

/**
 * Defect ids for one address line. Only line 0 is classified by callers today;
 * the function itself is position-agnostic.
 */
export function classifyAddressLine(line) {
  const value = clean(line).toLowerCase();
  if (!value) return [];
  return CTA_ADDRESS_MARKERS.some((marker) => value.includes(marker)) ? ['address-cta-junk'] : [];
}

/**
 * Is this string admissible as a contamination term at all?
 *
 * Lives HERE, beside the vocabulary it consults, rather than in
 * `contamination-scan.js` where it was written — `shared/` must not import from
 * `ai/`, and `template-identity-health.js` needs to ask the SAME question the
 * catalog asks. Two copies of this predicate is how a health check reports
 * "identity is scrubbable" about a catalog that dropped every name term.
 * `contamination-scan.js` re-exports it, so every existing caller is unchanged.
 */
export function isCatalogableTerm(value) {
  const term = clean(value);
  return term.length >= 4 && !GENERIC_SOURCE_TERMS.has(term.toLowerCase());
}

/**
 * True when a catalog term is short enough and common enough that blocking on
 * it would deadlock a compose.
 */
export function isOverbroadTerm(term) {
  const value = clean(term);
  return value.length <= OVERBROAD_MAX_LENGTH && OVERBROAD_TERM_STOPLIST.has(value.toLowerCase());
}

/**
 * Does this cataloged term cover the expected identity string?
 *
 * Containment is LITERAL (case- and whitespace-normalised), never squashed,
 * because that is what the scanner can actually find: `countTermOccurrences`
 * does case-insensitive substring matching on the rendered surface. The
 * squashed forms of `Bluffcitywebdesigns` and `Bluff City Web Designs` are
 * equal, but the first will never match the second in prose — and treating
 * them as equivalent here is precisely how a coverage gate would pass on the
 * flagship incident.
 *
 * A longer legal name still covers ("Bluff City Web Designs LLC" contains the
 * expected string), and so does a shorter authentic alias the prose uses.
 */
export function termCoversExpected(term, expected) {
  const a = clean(term).toLowerCase().replace(/\s+/g, ' ');
  const b = clean(expected).toLowerCase().replace(/\s+/g, ' ');
  if (!a || !b) return false;
  return a.includes(b) || b.includes(a);
}

/**
 * Why a string cannot serve as a business NAME, or `null` if it can.
 *
 * ## SU-5, and the two defects it covers
 *
 * `contractor-3` shipped `businessName: 'Holmes Contracting"'` — one trailing
 * double-quote — and threw `ContaminationDetectedError` for **100% of clients,
 * permanently**. The substitution pair is built from the literal, so it hunts
 * for `Holmes Contracting"`, which occurs in the markup only as the tail of
 * `alt="Holmes Contracting"`. It can never be cleanly replaced, the scan keeps
 * finding it at `high` severity, and the composition is refused forever.
 * Unreachable AND blocking at once — the `abshillbillygyros` deadlock SK-2
 * recorded, reappearing in the DATA rather than in the code.
 *
 * `testvalleydigital` carried `'www.testvalleydigital.com'`, which is the same
 * class one step on: a pair that can never match the prose the site prints.
 *
 * ## The URL rule is narrow ON PURPOSE
 *
 * A first version flagged `M.Media` — a real business in this corpus — because
 * `.media` is a TLD. Requiring the whole string to be lowercase is what
 * separates a domain from a name, since business names are capitalised and
 * domains conventionally are not. Condemning a correct name to catch a wrong
 * one is the trade this repo has refused twice already (address lines by
 * LENGTH, and the squash comparison in `classifyBusinessName`).
 *
 * Lives HERE so the extractor's candidate guard and the corpus write gate ask
 * ONE question. Two copies is how a producer starts emitting what its own gate
 * rejects.
 */
export function unusableNameReason(value) {
  if (typeof value !== 'string') return null;
  const raw = value;
  if (!raw.trim()) return null;
  if (/["<>\\]/.test(raw)) return 'contains a quote or angle bracket';
  if (raw !== raw.trim()) return 'has leading or trailing whitespace';
  if (isUrlShapedName(raw)) return 'is a URL, not a business name';
  return null;
}

/** True for `www.foo.com`, `https://foo.com` and `foo.com` — but not `M.Media`. */
export function isUrlShapedName(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (/^https?:\/\//i.test(raw) || /^www\./i.test(raw)) return true;
  // All-lowercase plus a common TLD. Capitalisation is the discriminator.
  return /^[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)*\.(?:com|net|org|io|co|uk|ca|dev|app|info|biz|us|au)$/.test(
    raw
  );
}
